import React, { useEffect, useState, useCallback } from 'react';
import styles from './ContentArea.module.css';
import SevenCsPanel from '../seven-cs/SevenCsPanel';
import ConceptMapView from '../concept-map/ConceptMapView';
import TranscriptPanel from '../transcript-drawer/TranscriptDrawer';
import { logStudyAction } from '../../services/study-log-service';

const SpeakerRenameBar = ({ deviceId, sessionId, onSpeakerChanged, readOnly = false }) => {
    const [speakers, setSpeakers] = useState([]);
    const [editing, setEditing] = useState(null);
    const [editValue, setEditValue] = useState('');

    // CUI 2026 comparison study: speaker renaming is hidden for all study
    // participants (both `edit` and `no_edit` conditions). Speaker renaming
    // is not part of the experimental manipulation, and exposing it adds a
    // confound where participants could mutate the speaker identity layer.
    // Speaker labels remain visible (informational); only the Rename button
    // is suppressed. Other roles (admin, TA, user) retain rename capability.
    const [isStudyParticipant, setIsStudyParticipant] = useState(false);
    useEffect(() => {
        (async () => {
            try {
                const res = await fetch('/api/v1/me');
                if (res.ok) {
                    const me = await res.json();
                    setIsStudyParticipant(me.role === 'study');
                }
            } catch (_) {}
        })();
    }, []);
    const canRename = !readOnly && !isStudyParticipant;

    const fetchSpeakers = useCallback(() => {
        if (!deviceId || !sessionId) return;
        fetch(`/api/v1/sessions/${sessionId}/devices/${deviceId}/speakers`)
            .then(r => r.ok ? r.json() : [])
            .then(data => setSpeakers(Array.isArray(data) ? data : []))
            .catch(() => {});
    }, [deviceId, sessionId]);

    useEffect(() => { fetchSpeakers(); }, [fetchSpeakers]);

    // CUI 2026 study: hide the entire speakers display for study participants.
    // The bar previously stayed visible (read-only) with the Rename buttons
    // suppressed, but the speaker labels themselves are not part of the
    // experimental affordance and add visual noise above the assessment.
    if (isStudyParticipant) return null;
    if (!speakers.length) return null;

    const handleSave = (speakerId) => {
        if (!editValue.trim()) return;
        fetch(`/api/speakers/${speakerId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ alias: editValue.trim() }),
        })
            .then(r => r.ok ? r.json() : null)
            .then(updated => {
                if (updated) {
                    setSpeakers(prev => prev.map(s => s.id === speakerId ? { ...s, alias: updated.alias } : s));
                    if (onSpeakerChanged) onSpeakerChanged();
                }
                setEditing(null);
            })
            .catch(() => setEditing(null));
    };

    return (
        <div className={styles.speakerBar}>
            <span className={styles.speakerBarLabel}>Speakers:</span>
            {speakers.map(s => (
                <span key={s.id} className={styles.speakerChip}>
                    {editing === s.id ? (
                        <>
                            <input
                                className={styles.speakerInput}
                                value={editValue}
                                onChange={e => setEditValue(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleSave(s.id)}
                                autoFocus
                                maxLength={64}
                            />
                            <button className={styles.speakerSaveBtn} onClick={() => handleSave(s.id)}>Save</button>
                            <button className={styles.speakerCancelBtn} onClick={() => setEditing(null)}>x</button>
                        </>
                    ) : (
                        <>
                            <span>{s.alias}</span>
                            {canRename && (
                                <button
                                    className={styles.speakerEditBtn}
                                    onClick={() => { setEditing(s.id); setEditValue(s.alias || ''); }}
                                    title="Rename speaker"
                                >Rename</button>
                            )}
                        </>
                    )}
                </span>
            ))}
        </div>
    );
};

const MOVE_LABELS = {
    probe: 'Probe', surface_tension: 'Surface Tension', redirect: 'Redirect',
    invite: 'Invite', synthesize: 'Synthesize', summarize_refocus: 'Summarize',
};
const MOVE_COLORS = {
    probe: '#3b82f6', surface_tension: '#ef4444', redirect: '#f59e0b',
    invite: '#8b5cf6', synthesize: '#10b981', summarize_refocus: '#06b6d4',
};

const FacilitationArchive = ({ deviceId }) => {
    const [messages, setMessages] = useState([]);
    const [collapsed, setCollapsed] = useState(false);

    useEffect(() => {
        if (!deviceId) return;
        fetch(`/api/facilitation/messages/${deviceId}`)
            .then(r => r.ok ? r.json() : [])
            .then(data => setMessages(Array.isArray(data) ? data : []))
            .catch(() => {});
    }, [deviceId]);

    if (!messages.length) return null;

    const formatTime = (ts) => {
        if (!ts) return '';
        const d = new Date(ts * 1000);
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className={styles.facilitationArchive}>
            <div className={styles.facilitationHeader} onClick={() => setCollapsed(c => !c)}>
                <span className={styles.facilitationIcon}>&#9672;</span>
                <span className={styles.facilitationTitle}>Facilitation Agent</span>
                <span className={styles.facilitationCount}>{messages.length} message{messages.length !== 1 ? 's' : ''}</span>
                <span className={styles.facilitationCollapse}>{collapsed ? '\u25B6' : '\u25BC'}</span>
            </div>
            {!collapsed && (
                <div className={styles.facilitationBody}>
                    {messages.map((msg, i) => {
                        const moveType = msg.move_type || 'probe';
                        const color = MOVE_COLORS[moveType] || '#64748b';
                        return (
                            <div key={i} className={styles.facilitationMsg}>
                                <div className={styles.facilitationMsgMeta}>
                                    <span className={styles.facilitationBadge} style={{ backgroundColor: color }}>
                                        {MOVE_LABELS[moveType] || moveType}
                                    </span>
                                    <span className={styles.facilitationTime}>{formatTime(msg.timestamp)}</span>
                                </div>
                                <p className={styles.facilitationText}>{msg.utterance || msg.message || ''}</p>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const ContentArea = ({ deviceId, deviceInfo, transcriptOpen, onTranscriptToggle, readOnly = false }) => {

    // Counter that increments when speakers are renamed, triggering transcript refetch
    const [speakerVersion, setSpeakerVersion] = useState(0);

    // CUI 2026 study: concept map section is hidden for study participants in both
    // conditions. Concept-map editability is already off for them; visually removing
    // the section avoids drawing attention to an artifact they cannot manipulate.
    const [isStudyParticipant, setIsStudyParticipant] = useState(false);
    useEffect(() => {
        (async () => {
            try {
                const res = await fetch('/api/v1/me');
                if (res.ok) {
                    const me = await res.json();
                    setIsStudyParticipant(me.role === 'study');
                }
            } catch (_) {}
        })();
    }, []);

    // Close transcript when device changes
    useEffect(() => {
        if (transcriptOpen) onTranscriptToggle(false);
    }, [deviceId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleTranscriptToggle = (open) => {
        logStudyAction(open ? 'transcript_open' : 'transcript_close', { session_device_id: parseInt(deviceId) });
        onTranscriptToggle(open);
    };

    const sessionName = deviceInfo?.session?.name;
    const deviceName = deviceInfo?.device?.name;

    return (
        <div className={styles.contentArea}>
            {!deviceId ? (
                <div className={styles.empty}>
                    <div className={styles.emptyInner}>
                        <span className={styles.emptyIcon}>◎</span>
                        <p className={styles.emptyText}>Use the left session navigator to explore</p>
                    </div>
                </div>
            ) : (
                <>
                    {/* Header */}
                    <div className={styles.contentHeader}>
                        {sessionName && (
                            <span className={styles.breadcrumb}>
                                {sessionName}{deviceName ? ` · ${deviceName}` : ''}
                            </span>
                        )}
                        <button
                            className={`${styles.transcriptBtn} ${transcriptOpen ? styles.transcriptBtnActive : ''}`}
                            onClick={() => handleTranscriptToggle(!transcriptOpen)}
                        >
                            Transcript
                        </button>
                    </div>

                    {/* Speaker rename bar (useful for Mode A auto-diarized sessions) */}
                    <SpeakerRenameBar
                        deviceId={deviceId}
                        sessionId={deviceInfo?.sessionId}
                        onSpeakerChanged={() => setSpeakerVersion(v => v + 1)}
                        readOnly={readOnly}
                    />

                    {/* Body */}
                    <div className={styles.scrollBody}>
                        {/* Assessment + optional transcript side-by-side */}
                        <div className={transcriptOpen ? styles.splitRow : styles.assessmentFull}>
                            <div className={transcriptOpen ? styles.assessmentCol : undefined}>
                                <SevenCsPanel
                                    sessionDeviceId={parseInt(deviceId)}
                                    sessionName={sessionName}
                                    deviceName={deviceName}
                                    singleColumn={transcriptOpen}
                                    transcriptOpen={transcriptOpen}
                                    readOnly={readOnly}
                                />
                            </div>
                            {transcriptOpen && (
                                <div className={styles.transcriptCol}>
                                    <TranscriptPanel
                                        deviceId={deviceId}
                                        sessionId={deviceInfo?.sessionId}
                                        speakerVersion={speakerVersion}
                                        onClose={() => handleTranscriptToggle(false)}
                                        readOnly={readOnly}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Facilitation agent archive (for devices that had facilitation enabled) */}
                        <FacilitationArchive deviceId={deviceId} />

                        {/* Concept map — hidden for study participants (both conditions) */}
                        {!isStudyParticipant && (
                            <div className={styles.conceptMapSection}>
                                <h2 className={styles.sectionTitle}>Visual Scaffolding</h2>
                                <ConceptMapView
                                    sessionId={deviceInfo?.sessionId || null}
                                    sessionDeviceId={parseInt(deviceId)}
                                    readOnly={readOnly}
                                />
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default ContentArea;
